import { ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Drop-in product image.
 *  - Pass `src` (or set it in config.js PRODUCT_IMAGES) to show a real photo.
 *  - With no `src`, it renders a premium studio placeholder at the SAME
 *    aspect ratio, so swapping in real photography never shifts the layout.
 */
export const ProductImage = ({
  src = "",
  alt = "Black glossy NFC Google Review countertop stand, approximately 100 by 150 mm",
  ratio = "aspect-[4/5]",
  className = "",
  priority = false,
  testId = "product-image",
}) => {
  if (src) {
    return (
      <div className={cn("relative overflow-hidden rounded-2xl bg-panel", ratio, className)}>
        <img
          src={src}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          className="h-full w-full object-cover"
          data-testid={testId}
        />
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      role="img"
      aria-label={alt}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-panel",
        ratio,
        className
      )}
    >
      {/* studio spotlight */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_18%,#ffffff_0%,#f3f3f5_45%,#e9e9ec_100%)]" />
      {/* proportional frame guide (100 × 150 mm ≈ 2:3) — not a mockup, just a placement guide */}
      <div className="absolute inset-0 flex items-center justify-center p-8">
        <div className="flex h-[78%] w-[52%] max-w-[240px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-ink/20 bg-white/40 backdrop-blur-sm">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-ink/5">
            <ImageIcon className="h-5 w-5 text-ink/40" strokeWidth={1.4} />
          </div>
          <div className="px-4 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/55">
              Your product photo
            </p>
            <p className="mt-1 text-[11px] text-ink/40">100 × 150 mm · black glossy stand</p>
          </div>
        </div>
      </div>
    </div>
  );
};
